import Component from '@glimmer/component'
import { capitalize } from '@ember/string'
import { colors } from '@colors'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import type { StadiumEventsStudentData } from 'teacher-ui-client/utils/student-events/fetchers/stadium-events-student'

interface Args {
  summaryTitle?: string
  data: StadiumEventsStudentData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class StadiumEventsStudent extends Component<Signature> {
  @service intl!: IntlService

  readonly eventTypes: string[] = ['spelling', 'grammar', 'vocabulary', 'usage']

  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.LightBlue

  get categories(): string[] {
    return this.eventTypes.map((eventType) => capitalize(eventType))
  }

  get colors(): string[] {
    const spelling = colors.oceanyBlue300
    const grammar = colors.oceanyBlue400
    const vocabulary = colors.juicyOrange300
    const usage = colors.purplyPink300
    return [spelling, grammar, vocabulary, usage]
  }

  get summaryStats(): { label: string; value: string }[] {
    const totalCount = this.args.data.summary.total_count
    const label = this.intl.t('reporting.charts.stadiumEvents.totalEventsCompleted')

    return [{ label, value: totalCount.toString() }]
  }

  get chartData(): SeriesColumnOptions[] {
    const { spelling, grammar, vocabulary, usage } = this.args.data.events
    const data = [spelling, grammar, vocabulary, usage]

    return [{ data, type: 'column' }]
  }

  <template>
    <div data-test-giraffe-stadium-events-student class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @data={{this.chartData}}
            @categories={{this.categories}}
            @colors={{this.colors}}
            @colorByPoint={{true}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default StadiumEventsStudent
